import React, { useState } from "react";

import { MdmDeviceDto, MdmObjectDeviceRelationDto, MdmObjectDto, useCreateDeviceMutation, useGetProjectQuery } from "gql/generated/types";
import { GET_OBJECT } from "gql/object.operations";
import { Dialog } from "primereact/dialog";
import { Link, useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { JodaTimeUtils } from "utils/joda-time-utils";
import { TableColumn } from "../utils/types";
import { apolloErrorToaster, renderDeviceStatus, successToaster } from "../utils/utils";
import DeviceForm from "./DeviceForm";
import ItemsTable from "./ItemsTable";

type ObjectDevicesProps = {
  object: MdmObjectDto
}

const ObjectDevices: React.FC<ObjectDevicesProps> = ({object}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { projectId } = useParams()
  const { data: projectData } = useGetProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });
  const [createDeviceMutation] = useCreateDeviceMutation({
    onCompleted: () => {
      successToaster(T("alert.device-created-successfully"));
      setDialogOpen(false);
    },
    onError: apolloErrorToaster,
  });

  const handleSubmit = (device: Partial<MdmDeviceDto>) => {
    const { objectRelation, project, ...restDevice } = device
    createDeviceMutation({
      variables: {
        device: { ...restDevice, roleInObject: objectRelation?.roleInObject },
        objectId: object.id!,
      },
      update: (cache, { data }) => {
        cache.updateQuery({ query: GET_OBJECT, variables: { id: object.id } }, oldData =>
          oldData
            ? {
                mdmObjectById: {
                  ...oldData?.mdmObjectById,
                  deviceRelations: [
                    ...(oldData?.mdmObjectById.deviceRelations ?? []),
                    { mdmDevice: data?.createMdmDevice },
                  ],
                },
              }
            : null
        );
      },
    });
  }

  const renderActions = (rowData: MdmObjectDeviceRelationDto) => (
    <Link to={`/mdm/${projectId}/devices/${rowData.mdmDevice!.id}`}>
      {rowData.mdmDevice?.identifier}
    </Link>
  );

  const columns: TableColumn<MdmObjectDeviceRelationDto>[] = [
    {
      field: "mdmDevice.identifier",
      header: T("word.identifier"),
      body: renderActions,
      sortable: true,
      columnKey: "identifier",
    },
    {
      field: "roleInObject",
      header: T("word.role-in-object"),
      body: rowData =>
        projectData?.mdmProjectById.configuration?.deviceConfiguration.measurementTypes?.find(
          type => type?.id === rowData.roleInObject
        )?.title ?? "",
      sortable: true,
      columnKey: "roleInObject",
    },
    {
      field: "mdmDevice.status.status",
      header: T("word.status"),
      body: data => renderDeviceStatus(data.mdmDevice),
      sortable: true,
      columnKey: "status",
    },
    {
      field: "mdmDevice.createdAt",
      header: T("word.created"),
      body: rowData => JodaTimeUtils.formatInstantAsLocal(rowData.mdmDevice?.modifiedAt),
      sortable: true,
      columnKey: "createdAt",
    },
    {
      field: "mdmDevice.modifiedAt",
      header: T("word.updated"),
      body: rowData => JodaTimeUtils.formatInstantAsLocal(rowData.mdmDevice?.modifiedAt),
      sortable: true,
      columnKey: "modifiedAt",
    },
  ];

  return (
    <div>
      <ItemsTable
        columns={columns}
        tableName="DevicesByObject"
        data={object.deviceRelations.map(d => ({ ...d, mdmObject: object })) ?? []}
        onToolbarBtnClick={() => setDialogOpen(true)}
        searchFields={["mdmDevice.identifier", "mdmDevice.tags", "roleInObject"]}
      />
      <Dialog
        onHide={() => setDialogOpen(false)}
        header={T("mdm.add-device")}
        visible={dialogOpen}
        style={{ width: "min(100vw, 800px)" }}
      >
        <DeviceForm
          onSubmit={handleSubmit}
          device={{ identifier: "", objectRelation: { mdmObject: object }, tags: [] }}
          newForm
          isDialog
          closeDialog={() => setDialogOpen(false)}
        />
      </Dialog>
    </div>
  );
}

export default ObjectDevices;